export const PERSIST_KEY = 'mapleos_state_v1';
export const STARTUP_GUARD_KEY = 'mapleos_startup_guard_v1';
export const AI_CHAT_TABS_PREFIX = 'ai-chat-tabs:';
const STARTUP_GUARD_WINDOW_MS = 90_000;

export function markOsStartupInProgress() {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(
    STARTUP_GUARD_KEY,
    JSON.stringify({
      startedAt: Date.now(),
      completed: false,
    })
  );
}

export function clearOsStartupInProgress() {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(
    STARTUP_GUARD_KEY,
    JSON.stringify({
      startedAt: Date.now(),
      completed: true,
    })
  );
}

export function wasPreviousOsStartupInterrupted() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const raw = localStorage.getItem(STARTUP_GUARD_KEY);
    if (!raw) {
      return false;
    }

    const parsed = JSON.parse(raw) as { startedAt?: number; completed?: boolean };
    if (parsed.completed) {
      return false;
    }

    if (typeof parsed.startedAt !== 'number') {
      return true;
    }

    return Date.now() - parsed.startedAt < STARTUP_GUARD_WINDOW_MS;
  } catch {
    return false;
  }
}

export function clearPersistedAiChatTabs() {
  if (typeof window === 'undefined') {
    return;
  }

  const keysToRemove: string[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(AI_CHAT_TABS_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

export function stripAppWindowsFromSnapshot<T extends { appId: string }>(windows: T[], appId: string) {
  return windows.filter((window) => window.appId !== appId);
}

export function removeAppWindowsFromSnapshot<T extends { appId: string }>(windows: T[], appId: string) {
  return stripAppWindowsFromSnapshot(windows, appId);
}

export function clearPersistedWindowStateForApp(appId: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as {
      windows?: Array<Record<string, unknown> & { appId?: string }>;
      nextZIndex?: number;
    };

    localStorage.setItem(
      PERSIST_KEY,
      JSON.stringify({
        ...parsed,
        windows: (parsed.windows || []).filter((window) => window.appId !== appId),
      })
    );
  } catch {
    localStorage.removeItem(PERSIST_KEY);
  }
}

export function clearAllPersistedWindowState() {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(PERSIST_KEY);
}

export function resetBrokenWindowsState() {
  if (typeof window === 'undefined') {
    return;
  }

  clearPersistedAiChatTabs();
  clearAllPersistedWindowState();

  window.dispatchEvent(new CustomEvent('mapleos:reset-broken-windows'));
}

export function applyInterruptedStartupRecovery<T extends { appId: string }>(windows: T[]) {
  clearPersistedAiChatTabs();
  return stripAppWindowsFromSnapshot(windows, 'ai-chat');
}